import React, { useState } from 'react'
import styled from 'styled-components'
import { BigNumber } from '@ethersproject/bignumber'
import {
  getGasPriceDisplay,
  CurrencySymbolImage,
  DownArrow,
  ConfirmButton,
  Modal,
  DeadlinesDisplay,
  TokenAmount,
  Amount,
  TokenAmountReceive,
  Symbol,
  OutputDisclaimer,
  Details,
  DownArrowContainer,
  getExecutionGasPrepayDisplay,
} from '../../common'
import { useEthers, useSendTransaction } from '../../hooks'
import { CurrencyValue, formatCurrency, Pair } from '../../model'
import { getRemoveLiquidityTransaction } from '../../transactions'
import { useGasPrice, useSettings } from '../../providers'
import { EXECUTE_WITHDRAW_GAS_LIMIT, INTEGRAL_LP_SYMBOL } from '../../constants'
import { getRemoveLiquidityFeeDisplay } from '../../utils'

interface Props {
  pair: Pair
  firstValue: CurrencyValue
  secondValue: CurrencyValue
  liquidity: BigNumber
  onSubmit: () => void
  onClose: () => void
}

export function ConfirmRemoveLiquidity({ pair, firstValue, secondValue, liquidity, onSubmit, onClose }: Props) {
  const { account, chainId } = useEthers()
  const sendTransaction = useSendTransaction()
  const { settings } = useSettings()
  const gasPrice = useGasPrice()
  const [sending, setSending] = useState(false)

  async function withdraw() {
    if (!account || !chainId || !gasPrice || !gasPrice.delayGasPrice) {
      return
    }
    setSending(true)
    try {
      await sendTransaction(
        getRemoveLiquidityTransaction(
          account,
          chainId,
          firstValue,
          secondValue,
          liquidity,
          gasPrice.delayGasPrice,
          pair,
          settings
        )
      )
      setSending(false)
      onSubmit()
    } catch (err) {
      setSending(false)
      if (err instanceof Error) {
        alert(err.message) // TODO: better error handling
      }
    }
  }

  const feeDisplay = getRemoveLiquidityFeeDisplay(pair)
  const gasPriceDisplay = getGasPriceDisplay(gasPrice)
  const executionGasPrepayDisplay = getExecutionGasPrepayDisplay(EXECUTE_WITHDRAW_GAS_LIMIT, settings, gasPrice)

  return (
    <Modal title="Confirm Withdraw" onClose={onClose}>
      <Content>
        <TokenAmount>
          <Amount>{formatCurrency(18, liquidity)}</Amount>
          <Symbol>
            <CurrencySymbolImageStyled symbol={INTEGRAL_LP_SYMBOL} />
            {pair.getToken0Symbol()}-{pair.getToken1Symbol()}
          </Symbol>
        </TokenAmount>
        <DownArrowContainer>
          <DownArrow />
        </DownArrowContainer>
        <TokenAmountReceive>
          <Amount>{firstValue.format()}</Amount>
          <Symbol>
            <CurrencySymbolImageStyled symbol={firstValue.currency.symbol} />
            {firstValue.currency.symbol}
          </Symbol>
        </TokenAmountReceive>
        <OutputDisclaimer>
          This is an estimated amount. You will receive {firstValue.currency.symbol} proportional to your LP token input
          and the pool's reserves at the time of execution.
        </OutputDisclaimer>
        <TokenAmountReceive>
          <Amount>{secondValue.format()}</Amount>
          <Symbol>
            <CurrencySymbolImageStyled symbol={secondValue.currency.symbol} />
            {secondValue.currency.symbol}
          </Symbol>
        </TokenAmountReceive>
        <OutputDisclaimer>
          This is an estimated amount. You will receive {secondValue.currency.symbol} proportional to your LP token
          input and the pool's reserves at the time of execution.
        </OutputDisclaimer>
        <Details>
          <li>
            <strong>Fees</strong>
            <span>{feeDisplay}</span>
          </li>
          <li>
            <strong>Execution Delay</strong>
            <span>Mining + 5 mins</span>
          </li>
          <li>
            <strong>Execution Gas Prepay</strong>
            <span>{executionGasPrepayDisplay}</span>
          </li>
          <li>
            <strong>Default Gas Price</strong>
            <span>{gasPriceDisplay}</span>
          </li>
        </Details>
        <ConfirmButton onClick={withdraw} sending={sending} />
        <DeadlinesDisplay />
      </Content>
    </Modal>
  )
}

const Content = styled.div`
  padding: 15px 20px;
`

const CurrencySymbolImageStyled = styled(CurrencySymbolImage)`
  margin-right: 5px;
`
